import { useMemo, useState } from 'react'

import { useForm, zodResolver } from '@mantine/form'
import { useDebouncedValue, useFocusWithin } from '@mantine/hooks'
import { nearestUsableTick } from '@uniswap/v3-sdk'
import { z } from 'zod'

import { useGetWalletBalance } from '@repo/common/queries/blockchain/useGetWalletBalance'
import {
  type InSideType,
  useGetPoolConfig,
} from '@repo/common/queries/fission_dex'
import { useActiveFundInfo } from '@repo/common/queries/funds'
import {
  max_price_tick,
  next_price_tick,
  price_to_usable_tick,
} from '@repo/common/queries/helpers/tick_helpers'
import { useIsSuccess } from '@repo/common/queries/helpers/utilities'
import {
  useGetLimitOrderDisabled,
  useGetMatchingLimitOrder,
} from '@repo/common/queries/limit_orders'
import { useGetSymbolToDecimal } from '@repo/common/queries/portfolio'
import {
  type FundSymbol,
  useGetTokenAddress,
} from '@repo/common/queries/products'

const default_reason = 'Enter an amount'

export function get_receiving_tokens({
  symbol_in,
  price,
  sell_tokens,
}: {
  symbol_in: FundSymbol | 'USDC'
  price: number | ''
  sell_tokens: number
}): number {
  if (price == '' || price == 0) {
    return 0
  }
  if (symbol_in == 'USDC') {
    return sell_tokens / price
  }
  return sell_tokens * price
}

// eslint-disable-next-line max-lines-per-function, complexity
export const useLimitOrderFields = ({
  fund_symbol,
  starting_in_side,
  starting_price,
  starting_tick,
}: {
  fund_symbol: FundSymbol
  starting_in_side: InSideType
  starting_price?: number
  starting_tick?: number
}) => {
  const [has_initialized, set_initialized] = useState(false)
  const form = useForm({
    mode: 'controlled',
    initialValues: {
      sell_tokens: 100,
      price: starting_price || 100,
      symbol_in: starting_in_side === 'USDC' ? 'USDC' : fund_symbol,
    } as {
      sell_tokens: number
      price: number
      symbol_in: FundSymbol | 'USDC'
    },
    validateInputOnChange: true,
    clearInputErrorOnChange: false,
    validate: (values) => zodResolver(get_schema(values))(values),
  })
  const symbol_in: FundSymbol | 'USDC' = form.getValues().symbol_in
  const symbol_out: FundSymbol | 'USDC' =
    symbol_in == 'USDC' ? fund_symbol : 'USDC'
  const receive_tokens = get_receiving_tokens(form.getValues())

  const { get_isSuccess, combine_isSuccess } = useIsSuccess()

  const { balance: balance_fund, isConnected: wallet_isConnected } =
    combine_isSuccess(useGetWalletBalance(fund_symbol))
  const { balance: balance_usdc } = combine_isSuccess(
    useGetWalletBalance('USDC'),
  )
  const balance_in = symbol_in == 'USDC' ? balance_usdc : balance_fund

  const symbol_to_decimal = combine_isSuccess(useGetSymbolToDecimal())
  const { data: fund } = useActiveFundInfo({ symbol: fund_symbol })
  const fund_address = fund?.token_address
  const usdc_address = useGetTokenAddress({ symbol: 'USDC' })
  const { data: pool } = combine_isSuccess(useGetPoolConfig(fund_symbol))

  const current_usable_tick = nearestUsableTick(
    pool?.tickCurrent ?? 0,
    pool?.tickSpacing ?? 1,
  )
  const current_price: number = form.getValues().price

  // eslint-disable-next-line complexity
  const prices: {
    price_lower: number
    price_upper: number
    price_tick: number
    // eslint-disable-next-line complexity
  } = useMemo(() => {
    let price_lower: number = 1
    let price_upper: number = 10_000
    let price_tick = current_usable_tick
    if (
      fund_address != null &&
      usdc_address != null &&
      symbol_to_decimal.isSuccess &&
      pool != null
    ) {
      if (current_price > 0) {
        if (current_price == starting_price && starting_tick != null) {
          price_tick = nearestUsableTick(starting_tick, pool?.tickSpacing ?? 1)
        } else {
          price_tick = price_to_usable_tick({
            price: current_price,
            fund_symbol,
            fund_address,
            usdc_address,
            symbol_to_decimal,
            tickSpacing: pool.tickSpacing,
          })
        }
      }

      if (symbol_in == 'USDC') {
        price_lower = 1.0
        price_upper = next_price_tick({
          price_tick: current_usable_tick,
          direction: 'down',
          fund_symbol,
          fund_address,
          usdc_address,
          symbol_to_decimal,
          tickSpacing: pool.tickSpacing,
        }).new_price
      } else {
        price_lower = next_price_tick({
          price_tick: current_usable_tick,
          direction: 'up',
          fund_symbol,
          fund_address,
          usdc_address,
          symbol_to_decimal,
          tickSpacing: pool.tickSpacing,
        }).new_price
        price_upper = max_price_tick({
          fund_symbol,
          fund_address,
          usdc_address,
          symbol_to_decimal,
        }).max_price
      }

      if (has_initialized == false) {
        if (starting_price == null) {
          form.setValues({
            price: symbol_in == 'USDC' ? price_upper : price_lower,
          })
        }
        set_initialized(true)
      }
    }
    return { price_lower, price_upper, price_tick }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    current_usable_tick,
    fund_address,
    usdc_address,
    symbol_to_decimal,
    pool,
    current_price,
    symbol_in,
    has_initialized,
    starting_price,
    starting_tick,
    fund_symbol,
    // form,
  ])

  const [debounced_disabled_values] = useDebouncedValue(
    {
      fund_symbol,
      price: current_price,
      tick: prices.price_tick,
    },
    500,
    { leading: true },
  )

  const disabled_query = useGetLimitOrderDisabled(debounced_disabled_values)

  const matching_order = useGetMatchingLimitOrder({
    fund_symbol,
    price: current_price,
    tick: prices.price_tick,
  })

  const order_max: number =
    disabled_query.maxes.max_tokens_per_order - (matching_order?.tokens ?? 0)

  const get_schema = (values: { symbol_in: FundSymbol | 'USDC' }) => {
    return z.object({
      price: z
        .number({ message: 'Enter sell price' })
        .min(prices.price_lower, {
          message: `Min price is ${prices.price_lower}`,
        })
        .max(prices.price_upper, {
          message: `Max price is ${prices.price_upper}`,
        }),
      sell_tokens: z
        .number({ message: default_reason })
        .positive()
        .max(values.symbol_in == 'USDC' ? balance_usdc : balance_fund, {
          message: `Not enough ${values.symbol_in} balance`,
        })
        .max(order_max, {
          message: 'Max tokens per order exceeded',
        })
        .max(disabled_query.maxes.max_tokens_per_tick, {
          message: 'Max tokens on this price exceeded',
        }),
    })
  }

  const max_sell_tokens = Math.min(
    order_max,
    disabled_query.maxes.max_tokens_per_tick,
  )

  const price_focus = useFocusWithin()
  const sell_tokens_focus = useFocusWithin()

  const is_loading = get_isSuccess() == false || has_initialized == false

  const form_valid = form.isValid() && !is_loading

  const error_reason =
    disabled_query.reason ||
    get_schema(form.getValues()).safeParse(form.getValues()).error?.errors[0]
      ?.message ||
    default_reason

  return {
    form,
    wallet_isConnected,
    form_valid,
    paused: disabled_query.disabled,
    error_reason,
    price_focus,
    sell_tokens_focus,
    matching_order,
    ...prices,
    max_sell_tokens,
    balance_in,
    symbol_out,
    receive_tokens,
    is_loading,
  }
}
